<template>
  <div>
    <div class="mt-sm d-flex">
      <img
        class="item-image"
        :class="size"
        :src="styleColor.images.front.url">
      <div class="ms-md w-100 small-text">
        <div class="line-item">
          <div>
            <span v-if="inlineSizeAndName">{{ shortName }}</span>
            <span v-else>Retail Value</span>
          </div>
          <del>${{ Math.round(styleColor.style.retailPrice) }}</del>
        </div>
        <div class="line-item">
          <div>
            <span v-if="inlineSizeAndName">Size {{ itemToPurchase.trueSize }}</span>
            <span v-else>Price</span>
          </div>
          <div>{{ formatPrice(price) }}</div>
        </div>
        <div
          v-for="lineItem in priceData?.v2.lineItems"
          :key="lineItem.description"
          class="line-item promo mt-sm">
          <div>{{ lineItem.description }}</div>
          <div>-{{ formatPrice(lineItem.amount) }}</div>
        </div>
        <div class="line-item mt-sm">
          <div>Tax</div>
          <div>{{ taxDisplay }}</div>
        </div>
        <div class="line-item mt-sm">
          <div
            v-if="purchased">
            Paid with {{ defaultPayment.brand }} ending in  {{ defaultPayment.last4 }}
          </div>
          <div
            v-else
            class="total">
            Total
          </div>
          <div
            class="total">
            {{ formatPrice(total) }}
          </div>
        </div>
        <hr
          v-if="priceData?.v2.otherItems.length > 0"
          class="w-100">
        <div
          v-for="otherItem in priceData?.v2.otherItems"
          :key="otherItem.description"
          class="line-item mt-sm text-plum">
          <div>
            <div class="semi-bold">
              {{ otherItem.description }}
            </div>
            <div
              v-if="otherItem.message"
              class="pt-xs">
              {{ otherItem.message }}
            </div>
          </div>
          <div v-if="otherItem.amount !== null">
            {{ formatPrice(otherItem.amount) }}
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!purchased"
      class="mt-sm mb-lg">
      <div
        v-if="!itemToPurchase.itemPromoEligible"
        class="small-text mt-lg mb-xl">
        This item is not eligible for purchase with promo codes
      </div>
      <GiftCardPromoCodeInput
        v-else
        :accepts="CodeInputType.Both"
        :disabled="disabled || !itemToPurchase.itemPromoEligible"
        :errors="formErrors"
        @applied="handleApplied"
        @applying="handleApplying"/>
    </div>
    <div
      v-if="!purchased && total > 0">
      <my-case-default-payment-updater/>
    </div>
  </div>
</template>

<script>
import { formatPrice } from '@/utils/stringParsing.js'
import MyCaseDefaultPaymentUpdater from '../common/MyCaseDefaultPaymentUpdater'
import GiftCardPromoCodeInput from '@/components/global/GiftCardPromoCodeInput.vue'

import { mapState, mapActions } from 'vuex'
import { CodeInputType } from '@/composables/useGiftCardPromoCode.js'

export default {
  components: {
    MyCaseDefaultPaymentUpdater,
    GiftCardPromoCodeInput
  },
  props: {
    itemToPurchase: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'default',
      validator: value => {
        return value.match(/(large|default)/)
      }
    },
    inlineSizeAndName: {
      type: Boolean,
      default: true
    },
    purchased: {
      type: Boolean,
      default: false
    }
  },
  setup () {
    return { CodeInputType }
  },
  data () {
    return {
      formErrors: [],
      appliedPromoCode: null,
      priceData: null
    }
  },
  computed: {
    ...mapState('closet', [
      'styleColorsMap'
    ]),
    ...mapState('client', [
      'defaultPayment'
    ]),
    hasPaymentMethod () {
      return Boolean(this.defaultPayment)
    },
    styleColor () {
      return this.styleColorsMap[this.itemToPurchase.styleColor]
    },
    shortName () {
      return `${this.styleColor.style.brand} ${this.styleColor.style.subcategory}`
    },
    taxDisplay () {
      if (this.priceData === null) { return '---' }
      return '$' + (this.priceData.v2.tax / 100).toFixed(2)
    },
    remainingCredit () {
      if (this.priceData === null) { return 0 }
      return this.priceData?.v2?.otherItems?.find(item => item.description === 'Remaining account credit')?.amount
    },
    price () {
      if (this.priceData === null) { return this.itemToPurchase.priceClient * 100 }
      return this.priceData.v2.client
    },
    total () {
      // this.itemToPurchase.priceClient needs a multiplier to make it equivalent to this.priceData.chargeAmount...
      if (this.priceData === null) { return this.itemToPurchase.priceClient * 100 }
      return this.priceData?.v2.chargeAmount
    }
  },
  mounted () {
    this.$emit('total-updated', this.total, this.remainingCredit)
    this.$emit('price-loading', true)
    this.getItemPrice({ 
      itemPk: this.itemToPurchase.id,
      promoCode: this.appliedPromoCode  // Include promo code if it exists
    }).then((result) => {
      this.priceData = result
      this.$emit('total-updated', this.total, this.remainingCredit)
    }).finally(() => {
      this.$emit('price-loading', false)
    })
  },
  methods: {
    ...mapActions('case', [
      'setMyCaseFlyoutName',
      'setMyCaseFlyoutContext'
    ]),
    ...mapActions('client', [
      'buyPackageItem',
      'getItemPrice'
    ]),
    formatPrice,
    async handleApplied (event) {
      this.$emit('price-loading', true)
      try {
        if (event.type === CodeInputType.GiftCard) {
          this.priceData = await this.getItemPrice({
            itemPk: this.itemToPurchase.id,
            promoCode: this.appliedPromoCode
          })
          this.$emit('total-updated', this.total, this.remainingCredit)
        } else if (event.type === CodeInputType.PromoCode) {
          this.formErrors = []
          this.priceData = null
          try {
            this.priceData = await this.getItemPrice({
              itemPk: this.itemToPurchase.id,
              promoCode: event.code
            })
            this.appliedPromoCode = event.code
            this.$emit('applied-promo', this.appliedPromoCode)
            this.$emit('total-updated', this.total)
          } catch (errors) {
            this.formErrors = Array.isArray(errors) ? errors : [errors]
            this.appliedPromoCode = null
          }
        }
      } finally {
        this.$emit('price-loading', false)
      }
    },
    handleApplying (isApplying) {
      if (isApplying) {
        this.formErrors = []
      }
      this.$emit('price-loading', isApplying)
    }
  }
}
</script>

<style lang="scss" scoped>
.item-image {
  height: 64px;
  width: 64px;
  border: $default-border;
  object-fit: contain;
  background: $white;

  &.large {
    height: 128px;
    width: 128px;
  }
}

.line-item {
  display: flex;
  justify-content: space-between;
}

.promo {
  color: $success-light;
  font-weight: $font-weight-bold;
}

.total {
  font-weight: $font-weight-bold;
}
</style>
